import React, { useState, useEffect } from 'react';
import { outlineApi } from '../../api/outlines';
import Button from '../Common/Button';
import Loading from '../Common/Loading';
import './Outline.css';

function VersionSection({ section, highlight }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`version-history__section ${highlight ? 'version-history__section--changed' : ''}`}>
      <button
        className="version-history__section-header"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="version-history__section-toggle">{expanded ? '▼' : '▶'}</span>
        <span className="version-history__section-name">{section.display_name}</span>
        {highlight && <span className="version-history__section-badge">Changed</span>}
      </button>
      {expanded && (
        <div className="version-history__section-content">
          {section.content ? (
            <div className="version-history__section-text">{section.content}</div>
          ) : (
            <div className="version-history__section-text version-history__section-text--empty">No content</div>
          )}
          {section.sub_sections && section.sub_sections.length > 0 && (
            <div className="version-history__sub-sections">
              {section.sub_sections.map((sub, i) => (
                <div key={sub.section_key || i} className="version-history__sub-section">
                  <strong>{sub.display_name}</strong>
                  {sub.content ? (
                    <div className="version-history__section-text">{sub.content}</div>
                  ) : (
                    <div className="version-history__section-text version-history__section-text--empty">No content</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CompareSection({ displayName, contentA, contentB, versionA, versionB }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="version-history__section version-history__section--changed">
      <button
        className="version-history__section-header"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="version-history__section-toggle">{expanded ? '▼' : '▶'}</span>
        <span className="version-history__section-name">{displayName}</span>
        <span className="version-history__section-badge">Changed</span>
      </button>
      {expanded && (
        <div className="version-history__compare-content">
          <div className="version-history__compare-panel version-history__compare-panel--old">
            <div className="version-history__compare-label">v{versionA}</div>
            <div className="version-history__section-text">
              {contentA || 'No content'}
            </div>
          </div>
          <div className="version-history__compare-panel version-history__compare-panel--new">
            <div className="version-history__compare-label">v{versionB}</div>
            <div className="version-history__section-text">
              {contentB || 'No content'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function VersionHistory({ outlineId, onExport }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [versionDetail, setVersionDetail] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    loadVersions();
  }, [outlineId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const response = await outlineApi.getVersions(outlineId);
      setVersions(response.data || []);
    } catch (err) {
      setError('Failed to load versions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setVersionDetail(null);
    setComparisonData(null);
    setDetailError(null);
  };

  const loadVersionDetail = async (versionNumber) => {
    try {
      setDetailLoading(true);
      setDetailError(null);
      const response = await outlineApi.getVersion(outlineId, versionNumber);
      setVersionDetail(response.data || response);
      setViewMode('detail');
    } catch (err) {
      setDetailError('Failed to load version details');
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const findSection = (versionData, sectionKey) => {
    const sections = versionData?.sections || [];
    for (const section of sections) {
      if (section.section_key === sectionKey) return section;
      if (section.sub_sections) {
        for (const sub of section.sub_sections) {
          if (sub.section_key === sectionKey) return sub;
        }
      }
    }
    return null;
  };

  const toggleVersionSelect = (versionNumber) => {
    if (!compareMode) {
      loadVersionDetail(versionNumber);
      return;
    }

    setSelectedVersions(prev => {
      if (prev.includes(versionNumber)) {
        return prev.filter(v => v !== versionNumber);
      }
      if (prev.length >= 2) {
        return [prev[1], versionNumber];
      }
      return [...prev, versionNumber];
    });
  };

  const handleCompare = async () => {
    if (selectedVersions.length !== 2) return;
    const [a, b] = selectedVersions.sort((x, y) => x - y);
    try {
      setDetailLoading(true);
      setDetailError(null);
      const [metaResponse, versionAResponse, versionBResponse] = await Promise.all([
        outlineApi.compareVersions(outlineId, a, b),
        outlineApi.getVersion(outlineId, a),
        outlineApi.getVersion(outlineId, b),
      ]);
      setComparisonData({
        meta: metaResponse.data || metaResponse,
        versionA: versionAResponse.data || versionAResponse,
        versionB: versionBResponse.data || versionBResponse,
        a,
        b,
      });
      setViewMode('compare');
    } catch (err) {
      setDetailError('Failed to load comparison');
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) return <Loading />;
  if (error) return <div className="version-history__error">{error}</div>;

  if (detailLoading) {
    return (
      <div className="version-history">
        <Loading />
      </div>
    );
  }

  if (detailError) {
    return (
      <div className="version-history">
        <div className="version-history__detail-error">
          <p>{detailError}</p>
          <button className="version-history__back-btn" onClick={handleBack}>
            Back to Versions
          </button>
        </div>
      </div>
    );
  }

  if (viewMode === 'detail' && versionDetail) {
    const sections = versionDetail.sections || [];
    return (
      <div className="version-history">
        <div className="version-history__detail-header">
          <button className="version-history__back-btn" onClick={handleBack}>
            &larr; Back to Versions
          </button>
          <h4>Version {versionDetail.version_number}</h4>
          <div className="version-history__item-actions">
            <Button
              size="small"
              variant="secondary"
              onClick={() => onExport(versionDetail.version_number, 'docx')}
            >
              DOCX
            </Button>
            <Button
              size="small"
              variant="secondary"
              onClick={() => onExport(versionDetail.version_number, 'markdown')}
            >
              MD
            </Button>
          </div>
        </div>
        <div className="version-history__detail-meta">
          <span>{sections.length} sections</span>
          {versionDetail.created_at && (
            <span> &middot; {new Date(versionDetail.created_at).toLocaleDateString()}</span>
          )}
        </div>
        {sections.length === 0 ? (
          <p className="version-history__empty">This version has no sections.</p>
        ) : (
          <div className="version-history__detail-sections">
            {sections.map((section, i) => (
              <VersionSection key={section.section_key || i} section={section} />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (viewMode === 'compare' && comparisonData) {
    const { meta, versionA, versionB, a, b } = comparisonData;
    const changedKeys = meta.sections_with_changes || [];
    return (
      <div className="version-history">
        <div className="version-history__detail-header">
          <button className="version-history__back-btn" onClick={handleBack}>
            &larr; Back to Versions
          </button>
          <h4>Compare v{a} &rarr; v{b}</h4>
        </div>
        <div className="version-history__compare-summary">
          <span>{changedKeys.length} section{changedKeys.length !== 1 ? 's' : ''} changed</span>
          {meta.sections_added !== undefined && (
            <span> &middot; {meta.sections_added} added</span>
          )}
          {meta.sections_removed !== undefined && (
            <span> &middot; {meta.sections_removed} removed</span>
          )}
        </div>
        {changedKeys.length === 0 ? (
          <p className="version-history__empty">No changes between these versions.</p>
        ) : (
          <div className="version-history__detail-sections">
            {changedKeys.map((key) => {
              const sectionA = findSection(versionA, key);
              const sectionB = findSection(versionB, key);
              const displayName = sectionB?.display_name || sectionA?.display_name || key;
              return (
                <CompareSection
                  key={key}
                  displayName={displayName}
                  contentA={sectionA?.content}
                  contentB={sectionB?.content}
                  versionA={a}
                  versionB={b}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Default: version list
  return (
    <div className="version-history">
      <div className="version-history__header">
        <h4>Version History</h4>
        <label className="version-history__compare-toggle">
          <input
            type="checkbox"
            checked={compareMode}
            onChange={(e) => {
              setCompareMode(e.target.checked);
              setSelectedVersions([]);
            }}
          />
          Compare Mode
        </label>
      </div>

      {versions.length === 0 ? (
        <p className="version-history__empty">No versions yet. Generate the outline to create the first version.</p>
      ) : (
        <ul className="version-history__list">
          {versions.map(version => (
            <li
              key={version.version_number}
              className={`version-history__item ${selectedVersions.includes(version.version_number) ? 'version-history__item--selected' : ''}`}
              onClick={() => toggleVersionSelect(version.version_number)}
            >
              <div className="version-history__item-header">
                <span className="version-history__version-number">v{version.version_number}</span>
                <span className="version-history__date">
                  {new Date(version.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="version-history__item-meta">
                {version.section_count} sections • {version.sessions_included_count || 0} workshops
              </div>
              <div className="version-history__item-actions">
                <Button
                  size="small"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onExport(version.version_number, 'docx');
                  }}
                >
                  DOCX
                </Button>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onExport(version.version_number, 'markdown');
                  }}
                >
                  MD
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {compareMode && selectedVersions.length === 2 && (
        <div className="version-history__compare-actions">
          <Button onClick={handleCompare} variant="primary">
            Compare v{Math.min(...selectedVersions)} with v{Math.max(...selectedVersions)}
          </Button>
        </div>
      )}
    </div>
  );
}

export default VersionHistory;
